import { Card, CardContent, Divider, Drawer, Grid, Tooltip, Typography } from '@material-ui/core';
import { CheckCircle, Error, GetApp, WatchLater } from '@material-ui/icons';
import { conciliacaoStatusDescriptions } from 'common/genericDescriptions';
import { useGenericStyles } from 'common/genericStyles';
import { ConciliacaoArquivoResponse, ConciliacaoStatus, Perfil } from 'common/genericTypes';
import { GenericToast, GenericToastKind } from 'components/genericToast';
import { WarningSign } from 'components/warningSign';
import { Button, MdSpace, SmSpace, TextButton } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { formatDate } from 'utils/date';

const Conciliacao: React.FC = () => {
  const dispatch = useDispatch();
  const genericClasses = useGenericStyles();
  const { getConciliacoesLista, getConciliacaoDownload, postConfirmarConciliacao } = useSelector(
    (state: RootState) => state.instrumentoApi
  );
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);

  const [conciliacoesAguardandoLiquidacao, setConciliacoesAguardandoLiquidacao] = useState<
    ConciliacaoArquivoResponse[]
  >([]);
  const [conciliacoesRestantes, setConciliacoesRestantes] = useState<ConciliacaoArquivoResponse[]>([]);
  const [selectedConciliacao, setSelectedConciliacao] = useState<ConciliacaoArquivoResponse>();
  const [conciliacaoConfirmada, setConciliacaoConfirmada] = useState<ConciliacaoArquivoResponse>();
  const [openConfimarConciliacaoDrawer, setOpenConfimarConciliacaoDrawer] = useState(false);

  const getStatusIcon = (atrasada: boolean, status: ConciliacaoStatus) => {
    if (atrasada) return <Error fontSize="small" className={genericClasses.warningIcon} />;
    switch (status) {
      case ConciliacaoStatus.AguardandoConciliacao:
      case ConciliacaoStatus.AguardandoDownload:
        return <WatchLater fontSize="small" className={genericClasses.warningIcon} />;
      case ConciliacaoStatus.Conciliado:
        return <CheckCircle fontSize="small" className={genericClasses.successIcon} />;
      default:
        return <></>;
    }
  };

  const getToolTipDescription = (atrasada: boolean, status: ConciliacaoStatus) => {
    if (atrasada) return 'Conciliação em Atraso';
    return conciliacaoStatusDescriptions[status];
  };

  const getConciliacoes = useCallback(async () => {
    try {
      const result = await getConciliacoesLista(dispatch, true);
      if (result) {
        setConciliacoesAguardandoLiquidacao(result.filter((_) => _.status === ConciliacaoStatus.AguardandoConciliacao));
        setConciliacoesRestantes(result.filter((_) => _.status !== ConciliacaoStatus.AguardandoConciliacao));
      }
    } catch (error) {}
  }, [dispatch, getConciliacoesLista]);

  const handleConfirmClick = async () => {
    try {
      if (selectedConciliacao) {
        await postConfirmarConciliacao(dispatch, selectedConciliacao?.conciliacaoInstrumentoFinanceiroArquivoId, true);
        setOpenConfimarConciliacaoDrawer(false);
        setConciliacaoConfirmada(selectedConciliacao);
        getConciliacoes();
      }
    } catch (error) {}
  };

  const handleConfirmarConciliacaoDrawerOpen = (conciliacao: ConciliacaoArquivoResponse) => {
    setSelectedConciliacao(conciliacao);
    setOpenConfimarConciliacaoDrawer(true);
  };

  const handleConfirmarConciliacaoDrawerClose = () => {
    setOpenConfimarConciliacaoDrawer(false);
  };

  const handleConciliacaoDownload = async (conciliacaoArquivoId: string, data: Date) => {
    try {
      const result = await getConciliacaoDownload(dispatch, conciliacaoArquivoId, true);
      const blob = new Blob([result], { type: 'blob' });
      const link = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      link.href = href;
      link.download = `conciliacao_${formatDate(data, 'DDMMYYYY')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(href);
      getConciliacoes();
    } catch (error) {}
  };

  useEffect(() => {
    getConciliacoes();
  }, [getConciliacoes]);

  return (
    <>
      {!!conciliacaoConfirmada && (
        <>
          <GenericToast kind={GenericToastKind.Success} onDismiss={() => setConciliacaoConfirmada(undefined)}>
            <Typography variant="body2" display={'inline'}>
              {'Conciliação de'}
            </Typography>
            &nbsp;
            <Typography display={'inline'}>{formatDate(conciliacaoConfirmada.data)}</Typography>
            &nbsp;
            <Typography variant="body2" display={'inline'}>
              {'confirmada com sucesso'}
            </Typography>
          </GenericToast>
          <MdSpace />
        </>
      )}
      {!!conciliacoesAguardandoLiquidacao?.length && (
        <>
          <Typography variant="h3">{'Aguardando confirmação'}</Typography>
          <SmSpace />
          <Grid container spacing={2}>
            {conciliacoesAguardandoLiquidacao.map((conciliacao) => (
              <Grid key={conciliacao.conciliacaoInstrumentoFinanceiroArquivoId} item sm={12} lg={6}>
                <Card variant="outlined">
                  <CardContent style={{ padding: 10 }}>
                    <Grid container justify="space-between" alignItems="center" spacing={2}>
                      <Grid item container alignItems="center" sm>
                        <Tooltip
                          title={getToolTipDescription(conciliacao.atrasada, conciliacao.status)}
                          aria-label={getToolTipDescription(conciliacao.atrasada, conciliacao.status)}
                        >
                          {getStatusIcon(conciliacao.atrasada, conciliacao.status)}
                        </Tooltip>
                        <Typography variant="body1" display={'inline'} style={{ marginLeft: 10 }}>
                          {formatDate(conciliacao.data)}
                        </Typography>
                      </Grid>
                      {!isUsuarioLeitura && (
                        <Grid item>
                          <TextButton
                            className={genericClasses.textButton}
                            onClick={() => handleConfirmarConciliacaoDrawerOpen(conciliacao)}
                          >
                            {'Confirmar Conciliação'}
                          </TextButton>
                          <Button
                            startIcon={<GetApp fontSize={'small'} />}
                            onClick={() =>
                              handleConciliacaoDownload(
                                conciliacao.conciliacaoInstrumentoFinanceiroArquivoId,
                                conciliacao.data
                              )
                            }
                          >
                            {'Download'}
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <MdSpace />
          <Divider variant="fullWidth" />
          <MdSpace />
        </>
      )}
      {!!conciliacoesRestantes?.length && (
        <Grid container spacing={2}>
          {conciliacoesRestantes.map((conciliacao) => (
            <Grid key={conciliacao.conciliacaoInstrumentoFinanceiroArquivoId} item sm={12} lg={6}>
              <Card variant="outlined">
                <CardContent style={{ padding: 10 }}>
                  <Grid container justify="space-between" alignItems="center" spacing={2}>
                    <Grid item container alignItems="center" sm>
                      <Tooltip
                        title={getToolTipDescription(conciliacao.atrasada, conciliacao.status)}
                        aria-label={getToolTipDescription(conciliacao.atrasada, conciliacao.status)}
                      >
                        {getStatusIcon(conciliacao.atrasada, conciliacao.status)}
                      </Tooltip>
                      <Typography variant="body1" display={'inline'} style={{ marginLeft: 10 }}>
                        {formatDate(conciliacao.data)}
                      </Typography>
                    </Grid>
                    {!isUsuarioLeitura && (
                      <Grid item>
                        <Button
                          startIcon={<GetApp fontSize={'small'} />}
                          onClick={() =>
                            handleConciliacaoDownload(
                              conciliacao.conciliacaoInstrumentoFinanceiroArquivoId,
                              conciliacao.data
                            )
                          }
                        >
                          {'Download'}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <Drawer
        anchor="right"
        open={openConfimarConciliacaoDrawer}
        onClose={handleConfirmarConciliacaoDrawerClose}
        PaperProps={{ style: { padding: '40px', width: 465 } }}
      >
        <Grid container>
          <Grid item md={12}>
            <Typography variant="h3">{'Confirmar Conciliação'}</Typography>
            <SmSpace />
            <Divider variant="fullWidth" />
            <SmSpace />
            <Typography variant="body2">{`Tem certeza que deseja confirmar a conciliação do dia ${
              selectedConciliacao?.data ? formatDate(selectedConciliacao.data) : ''
            }?`}</Typography>
            <SmSpace />
            <WarningSign message="Ao clicar em confirmar, o Participante declara, desde já, que a Laqus forneceu todas as informações necessárias, bem como adotou todos os procedimentos para assegurar a Conciliação Diária, nos termos do Art. 4°, § 4º, Inciso II e do Art. 38 da Seção III – Conciliação, da ICVM 541." />
            <SmSpace />
            <Button onClick={handleConfirmClick} variant={'contained'} color={'primary'} fullWidth>
              {'Confirmar'}
            </Button>
            <SmSpace />
            <Button onClick={handleConfirmarConciliacaoDrawerClose} variant={'outlined'} fullWidth>
              {'Cancelar'}
            </Button>
          </Grid>
        </Grid>
      </Drawer>
    </>
  );
};

export default Conciliacao;
